<!-- Settings — groups exactly as IDEATION Key screens 6. Screens role. -->
<template>
  <IonPage>
    <AppHeader title="Settings" back="/tabs/pager" />

    <IonContent :fullscreen="true" class="pm-scr-content">
      <!-- Your pager -->
      <IonList inset>
        <IonListHeader>
          <IonLabel>Your pager<p>Your number, and how to keep it.</p></IonLabel>
        </IonListHeader>
        <IonItem>
          <IonInput
            v-model="handle"
            label="Your name"
            label-placement="stacked"
            :maxlength="12"
            autocapitalize="characters"
            placeholder="WHAT SHOULD FRIENDS SEE?"
            helper-text="Friends see this instead of your number. Up to 12 letters."
            :error-text="saveHandle.error.value"
            :class="{ 'ion-invalid ion-touched': !!saveHandle.error.value }"
            class="pm-scr-upper"
            @ion-input="handle = String($event.target.value || '').toUpperCase().replace(/[^A-Z0-9 ._-]/g, '')"
            @ion-blur="saveHandle.run()"
          />
        </IonItem>
        <IonItem>
          <IonLabel>
            <h3>Pager number</h3>
            <p class="pm-scr-lcd-inline">{{ myNumber }}</p>
          </IonLabel>
          <IonButton slot="end" fill="outline" @click="copyNumber.run()">Copy</IonButton>
        </IonItem>
        <IonItem button detail @click="cardOpen = true">
          <IonLabel>Activation code<p>The code that brings your number to a new phone.</p></IonLabel>
        </IonItem>
        <IonItem button detail :disabled="restoreAction.busy.value" @click="askRestore">
          <IonLabel>
            {{ restoreAction.busy.value ? 'Restoring…' : 'Restore a number' }}
            <p>Use an activation code from another phone.</p>
          </IonLabel>
        </IonItem>
        <p v-if="restoreAction.error.value" class="field-error pm-scr-item-error" role="alert">{{ restoreAction.error.value }}</p>
        <IonItem button detail :disabled="rotate.busy.value" @click="askRotate">
          <IonLabel>
            {{ rotate.busy.value ? 'Making a new link…' : 'New invite link' }}
            <p>The old link and QR code stop working.</p>
          </IonLabel>
        </IonItem>
        <p v-if="rotate.error.value" class="field-error pm-scr-item-error" role="alert">{{ rotate.error.value }}</p>
      </IonList>

      <!-- Beeps -->
      <IonList inset>
        <IonListHeader>
          <IonLabel>Beeps<p>How your pager tells you a page arrived.</p></IonLabel>
        </IonListHeader>
        <IonItem lines="none">
          <IonLabel :id="`${uid}-set-alert`">Alert</IonLabel>
        </IonItem>
        <IonItem>
          <IonSegment
            :value="settings.alertMode"
            :aria-labelledby="`${uid}-set-alert`"
            class="pm-scr-segment"
            @ion-change="setAlertMode($event.detail.value)"
          >
            <IonSegmentButton value="beep"><IonLabel>BEEP</IonLabel></IonSegmentButton>
            <IonSegmentButton value="vibrate"><IonLabel>VIBRATE</IonLabel></IonSegmentButton>
            <IonSegmentButton value="silent"><IonLabel>SILENT</IonLabel></IonSegmentButton>
          </IonSegment>
        </IonItem>
        <IonItem>
          <IonLabel>
            <h3>Notifications</h3>
            <p>{{ notifText }}</p>
          </IonLabel>
          <IonButton
            v-if="notif !== 'granted' && notif !== 'unavailable'"
            slot="end"
            fill="outline"
            :disabled="turnOn.busy.value"
            @click="turnOn.run()"
          >
            Turn on
          </IonButton>
        </IonItem>
      </IonList>

      <!-- Display -->
      <IonList inset>
        <IonListHeader>
          <IonLabel>Display<p>Make the LCD easier to read.</p></IonLabel>
        </IonListHeader>
        <IonItem>
          <IonToggle :checked="!!settings.highContrast" @ion-change="setSetting('highContrast', $event.detail.checked)">
            High-contrast LCD
          </IonToggle>
        </IonItem>
        <IonItem lines="none">
          <IonLabel :id="`${uid}-set-size`">LCD text size</IonLabel>
        </IonItem>
        <IonItem>
          <IonSegment
            :value="String(settings.textSize || 1)"
            :aria-labelledby="`${uid}-set-size`"
            class="pm-scr-segment"
            @ion-change="setSetting('textSize', Number($event.detail.value))"
          >
            <IonSegmentButton v-for="s in SIZES" :key="s.value" :value="s.value" :aria-label="s.label">
              <IonLabel>{{ s.short }}</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonItem>
        <IonItem>
          <IonToggle :checked="!!settings.largeControls" @ion-change="setSetting('largeControls', $event.detail.checked)">
            Large controls
            <p class="pm-scr-toggle-note">Big labelled buttons instead of the device keys.</p>
          </IonToggle>
        </IonItem>
      </IonList>

      <!-- Presets -->
      <IonList inset>
        <IonListHeader>
          <IonLabel>Presets<p>One-tap lines in New page.</p></IonLabel>
        </IonListHeader>
        <IonItem v-for="(p, i) in presets" :key="p.id" class="pm-scr-preset">
          <button type="button" class="pm-scr-row-main pm-scr-preset-text" :aria-label="`Edit preset ${p.text}`" @click="editPreset(p)">
            <PageText :body="p.text" />
          </button>
          <div slot="end" class="pm-scr-icon-row">
            <IonButton fill="clear" :disabled="i === 0" :aria-label="`Move ${p.text} up`" @click="move(i, i - 1)">
              <IonIcon slot="icon-only" :icon="arrowUp" />
            </IonButton>
            <IonButton fill="clear" :disabled="i === presets.length - 1" :aria-label="`Move ${p.text} down`" @click="move(i, i + 1)">
              <IonIcon slot="icon-only" :icon="arrowDown" />
            </IonButton>
            <IonButton fill="clear" color="danger" :aria-label="`Delete preset ${p.text}`" @click="removePreset(p.id)">
              <IonIcon slot="icon-only" :icon="trashOutline" />
            </IonButton>
          </div>
        </IonItem>
        <IonItem v-if="!presets.length">
          <IonLabel class="pm-scr-muted">No presets yet.</IonLabel>
        </IonItem>
        <IonItem button @click="addPresetPrompt">
          <IonIcon slot="start" :icon="addOutline" aria-hidden="true" />
          <IonLabel>Add a preset</IonLabel>
        </IonItem>
      </IonList>

      <!-- Your pages -->
      <IonList inset>
        <IonListHeader>
          <IonLabel>Your pages<p>Everything here lives only on this phone.</p></IonLabel>
        </IonListHeader>
        <IonItem>
          <dl class="pm-scr-odometer" :class="glassClass">
            <div><dt>SENT</dt><dd>{{ pad(stats.sent) }}</dd></div>
            <div><dt>RECEIVED</dt><dd>{{ pad(stats.received) }}</dd></div>
            <div><dt>TOP CODE</dt><dd>{{ stats.topCode || '---' }}</dd></div>
          </dl>
        </IonItem>
        <IonItem button detail :disabled="exportAction.busy.value" @click="exportAction.run()">
          <IonLabel>{{ exportAction.busy.value ? 'Exporting…' : 'Export history' }}<p>A plain-text file of every page.</p></IonLabel>
        </IonItem>
        <p v-if="exportAction.error.value" class="field-error pm-scr-item-error" role="alert">{{ exportAction.error.value }}</p>
        <IonItem button :disabled="wipe.busy.value" @click="askWipe">
          <IonLabel color="danger">Delete all pages on this phone</IonLabel>
        </IonItem>
      </IonList>

      <!-- Blocked -->
      <IonList inset>
        <IonListHeader>
          <IonLabel>Blocked<p>Their pages and requests never reach you.</p></IonLabel>
        </IonListHeader>
        <IonItem v-for="f in blockedFriends" :key="f.number">
          <IonLabel>{{ friendName(f.number) }}<p v-if="friendSecondary(f.number)">{{ friendSecondary(f.number) }}</p></IonLabel>
          <IonButton slot="end" fill="outline" :disabled="unblockBusy === f.number" @click="unblock(f)">Unblock</IonButton>
        </IonItem>
        <IonItem v-if="!blockedFriends.length">
          <IonLabel class="pm-scr-muted">No one is blocked.</IonLabel>
        </IonItem>
        <p v-if="unblockError" class="field-error pm-scr-item-error" role="alert">{{ unblockError }}</p>
      </IonList>

      <!-- Privacy -->
      <IonList inset>
        <IonListHeader>
          <IonLabel>Privacy<p>What happens to your pages.</p></IonLabel>
        </IonListHeader>
        <IonItem>
          <IonLabel class="ion-text-wrap pm-scr-para">
            Pages are held on our server only until your pager collects them, then deleted. Undelivered pages are deleted after 30 days. Your history lives only on this phone. We never see your contacts, phone number or email, and there are no ads or trackers.
          </IonLabel>
        </IonItem>
        <IonItem href="/privacy" target="_blank" rel="noopener" detail>
          <IonLabel>Privacy policy</IonLabel>
        </IonItem>
      </IonList>

      <!-- Help -->
      <IonList inset>
        <IonListHeader>
          <IonLabel>Help<p>Codes, how it works, and who to ask.</p></IonLabel>
        </IonListHeader>
        <IonItem button detail @click="codebookOpen = true"><IonLabel>Codebook</IonLabel></IonItem>
        <IonItem button detail @click="howOpen = true"><IonLabel>How paging works</IonLabel></IonItem>
        <IonItem href="mailto:support@harfredlabs.com?subject=Page%20Me%20support" detail>
          <IonLabel>Contact support<p>support@harfredlabs.com</p></IonLabel>
        </IonItem>
        <IonItem href="/about" target="_blank" rel="noopener" detail><IonLabel>About</IonLabel></IonItem>
        <IonItem href="/terms" target="_blank" rel="noopener" detail><IonLabel>Terms</IonLabel></IonItem>
      </IonList>

      <!-- Danger -->
      <IonList inset>
        <IonListHeader>
          <IonLabel>Danger<p>Can't be undone.</p></IonLabel>
        </IonListHeader>
        <IonItem button :disabled="disconnectAction.busy.value" @click="askDisconnect">
          <IonLabel color="danger">
            {{ disconnectAction.busy.value ? 'Disconnecting…' : 'Disconnect this number' }}
            <p>Deletes your pager, friendships and undelivered pages from our server, and everything on this phone.</p>
          </IonLabel>
        </IonItem>
        <p v-if="disconnectAction.error.value" class="field-error pm-scr-item-error" role="alert">{{ disconnectAction.error.value }}</p>
      </IonList>

      <div class="pm-scr-bottom-space" />
    </IonContent>

    <ActivationCard :is-open="cardOpen" @close="cardOpen = false" />
    <CodebookSheet :is-open="codebookOpen" :insertable="false" @close="codebookOpen = false" />

    <IonModal
      :is-open="howOpen"
      :initial-breakpoint="1"
      :breakpoints="[0, 1]"
      class="pm-scr-sheet pm-scr-sheet--auto"
      @will-present="howOpener = activeEl()"
      @did-dismiss="closeHow"
    >
      <div class="pm-scr-sheet-body">
        <div class="pm-scr-sheet-head">
          <h2 class="section-title">How paging works</h2>
          <IonButton fill="clear" @click="howOpen = false">Done</IonButton>
        </div>
        <ol class="pm-scr-how">
          <li><strong>You have a pager number.</strong> Give it to friends, or share your invite link — anyone who opens the link is paired straight away.</li>
          <li><strong>Page them.</strong> Up to 160 characters: digits, words or any emoji. Codes like 143 still mean what they meant in 1996.</li>
          <li><strong>The network holds it.</strong> Your page waits on our server until their pager collects it, then it's deleted there.</li>
          <li><strong>Marks tell you where it is.</strong> … queued on your phone · ✓ sent to the network · D delivered to their pager · X expired or not delivered.</li>
          <li><strong>Only friends can page you.</strong> Anyone else lands in Pending, where you Accept or Block.</li>
          <li><strong>It's a toy pager.</strong> Not for emergencies — in an emergency, call your local emergency number.</li>
        </ol>
      </div>
    </IonModal>
  </IonPage>
</template>

<script setup>
import { userMessage } from '../components/user-message.js';
import { ref, computed, watch, onMounted, useId } from 'vue';
import { useRouter } from 'vue-router';
import {
  IonPage, IonContent, IonList, IonListHeader, IonItem, IonLabel, IonInput, IonButton, IonIcon,
  IonSegment, IonSegmentButton, IonToggle, IonModal, alertController,
} from '@ionic/vue';
import { arrowUp, arrowDown, trashOutline, addOutline } from 'ionicons/icons';
import { AppHeader, useAction, toast } from '../core/ui/index.js';
import { isNative, plugin } from '../core/native.js';
import { shareOrDownload } from '../core/export.js';
import {
  me, settings, currentDevice, presets, stats, blockedFriends,
  friendName, friendSecondary, setHandle, setAlertMode, setSetting, restore, rotateInvite,
  addPreset, updatePreset, removePreset, movePreset, exportHistory, deleteAllPages,
  unblockFriend, enableBeeps, disconnect,
} from '../store.js';
import { formatNumber } from '../pm/format.js';
import { validateBody } from '../pm/text.js';
import PageText from '../components/PageText.vue';
import ActivationCard from '../components/ActivationCard.vue';
import CodebookSheet from '../components/CodebookSheet.vue';

// Per-instance ids: Ionic keeps earlier pages in the DOM, so a fixed id
// would repeat and break label/for pairing.
const uid = `pm${useId()}`;

const router = useRouter();

const SIZES = [
  { value: '1', short: 'S', label: 'Small' },
  { value: '1.25', short: 'M', label: 'Medium' },
  { value: '1.5', short: 'L', label: 'Large' },
  { value: '2', short: 'XL', label: 'Extra large' },
];

const cardOpen = ref(false);
const codebookOpen = ref(false);
const howOpen = ref(false);
let howOpener = null;
const activeEl = () => document.activeElement;
function closeHow() {
  howOpen.value = false;
  const el = howOpener;
  howOpener = null;
  try { el?.focus?.(); } catch { /* gone */ }
}

const myNumber = computed(() => (me.value?.number ? formatNumber(me.value.number) : '—'));
const glassClass = computed(() => (settings.highContrast ? 'pm-scr-glass--hc' : `pm-scr-glass--${currentDevice.value?.lcd || 'classic'}`));
const pad = (n) => String(n ?? 0).padStart(4, '0');

function must(r, fallback) {
  if (r && r.ok === false) throw new Error(userMessage(r, fallback));
  return r;
}

// ── Your pager ──────────────────────────────────────────────────────────
const handle = ref(me.value?.handle || '');
watch(() => me.value?.handle, (h) => { handle.value = h || ''; });
const saveHandle = useAction(async () => {
  const h = handle.value.trim().toUpperCase();
  if (h === (me.value?.handle || '')) return;
  must(await setHandle(h), "Couldn't save your name. Try again.");
}, { done: 'Name saved' });

const copyNumber = useAction(async () => {
  await navigator.clipboard.writeText(myNumber.value);
}, { done: 'Number copied' });

const restoreAction = useAction(async (code) => {
  must(await restore(code), "That code didn't work. Check it and try again.");
  router.replace('/tabs/pager');
}, { done: 'Number restored', weight: 'pop' });

async function askRestore() {
  // Checked here, before anything is replaced: twelve letters and digits,
  // dashes and spaces optional. A wrong shape keeps the alert open and says so.
  const base = 'Enter the activation code from your printed card or notes.';
  const a = await alertController.create({
    header: 'Restore a number',
    message: base,
    inputs: [{ name: 'code', type: 'text', placeholder: 'K7QX-9MFD-2HTP', attributes: { autocapitalize: 'characters', autocomplete: 'off', spellcheck: false, 'aria-label': 'Activation code' } }],
    buttons: [
      { text: 'Cancel', role: 'cancel' },
      {
        text: 'Next',
        role: 'confirm',
        handler: (values) => {
          const raw = String(values?.code || '').toUpperCase().replace(/[\s-]/g, '');
          if (/^[A-Z0-9]{12}$/.test(raw)) return true;
          a.message = raw
            ? `${base} (That's ${raw.length} characters — a code has 12, like K7QX-9MFD-2HTP.)`
            : `${base} (Type the code first.)`;
          return false;
        },
      },
    ],
  });
  await a.present();
  const { role, data } = await a.onDidDismiss();
  const code = String(data?.values?.code || '').trim().toUpperCase();
  if (role !== 'confirm' || !code) return;

  const c = await alertController.create({
    header: 'Restore this number?',
    message: 'This replaces the number on this phone. Its friends and pages stay behind.',
    buttons: [{ text: 'Cancel', role: 'cancel' }, { text: 'Restore', role: 'destructive' }],
  });
  await c.present();
  const res = await c.onDidDismiss();
  if (res.role === 'destructive') restoreAction.run(code);
}

const rotate = useAction(async () => {
  must(await rotateInvite(), "Couldn't make a new link. Try again.");
}, { done: 'New invite link ready' });

async function askRotate() {
  const a = await alertController.create({
    header: 'Make a new invite link?',
    message: "Your old link and QR code stop working. Friends you already have stay.",
    buttons: [{ text: 'Cancel', role: 'cancel' }, { text: 'New link', role: 'confirm' }],
  });
  await a.present();
  const { role } = await a.onDidDismiss();
  if (role === 'confirm') rotate.run();
}

// ── Beeps ───────────────────────────────────────────────────────────────
// 'granted' | 'denied' | 'prompt' | 'unavailable'
const notif = ref(isNative ? 'prompt' : 'unavailable');
async function readNotif() {
  if (!isNative) { notif.value = 'unavailable'; return; }
  try {
    const r = await plugin('PushNotifications')?.checkPermissions?.();
    notif.value = r?.receive === 'granted' ? 'granted' : r?.receive === 'denied' ? 'denied' : 'prompt';
  } catch { notif.value = 'prompt'; }
}
onMounted(readNotif);
const notifText = computed(() => ({
  granted: 'On — your pager beeps even when Page Me is closed.',
  denied: "Off. To turn them on, open your phone's Settings, find Page Me, and allow notifications.",
  prompt: 'Off — Page Me only beeps while it is open.',
  unavailable: 'Beeps only while Page Me is open.',
}[notif.value]));

const turnOn = useAction(async () => {
  const r = await enableBeeps();
  notif.value = r === 'granted' ? 'granted' : r === 'denied' ? 'denied' : 'unavailable';
});

// ── Presets ─────────────────────────────────────────────────────────────
async function presetPrompt(header, value = '') {
  let err = '';
  for (;;) {
    const a = await alertController.create({
      header,
      message: err || 'Up to 160 characters. Emoji are fine.',
      inputs: [{ name: 'text', type: 'text', value, attributes: { autocapitalize: 'characters', 'aria-label': 'Preset text' } }],
      buttons: [{ text: 'Cancel', role: 'cancel' }, { text: 'Save', role: 'confirm' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    if (role !== 'confirm') return null;
    value = String(data?.values?.text || '').trim();
    err = validateBody(value) || '';
    if (!err) return value;
  }
}
async function addPresetPrompt() {
  const text = await presetPrompt('Add a preset');
  if (text) await addPreset(text);
}
async function editPreset(p) {
  const text = await presetPrompt('Edit preset', p.text);
  if (text && text !== p.text) await updatePreset(p.id, text);
}
function move(from, to) {
  movePreset(from, to);
  // Keep focus on the moved row's same button so repeated moves are easy.
}

// ── Your pages ──────────────────────────────────────────────────────────
const exportAction = useAction(async () => {
  const text = await exportHistory();
  const stamp = new Date().toISOString().slice(0, 10);
  await shareOrDownload(new Blob([text], { type: 'text/plain' }), `page-me-history-${stamp}.txt`, 'Page Me history');
});

const wipe = useAction(async () => {
  must(await deleteAllPages(), "Couldn't delete your pages. Try again.");
}, { done: 'All pages deleted', weight: 'pop' });

async function askWipe() {
  const a = await alertController.create({
    header: 'Delete all pages on this phone?',
    message: "Every page you've sent and received is removed from this phone. This can't be undone. Your friends keep their copies.",
    buttons: [{ text: 'Cancel', role: 'cancel' }, { text: 'Delete all', role: 'destructive' }],
  });
  await a.present();
  const { role } = await a.onDidDismiss();
  if (role === 'destructive') wipe.run();
}

// ── Blocked ─────────────────────────────────────────────────────────────
const unblockBusy = ref('');
const unblockError = ref('');
async function unblock(f) {
  if (unblockBusy.value) return;
  unblockBusy.value = f.number;
  unblockError.value = '';
  try {
    must(await unblockFriend(f.number), "Couldn't unblock. Try again.");
    toast(`${friendName(f.number)} unblocked`);
  } catch (e) {
    unblockError.value = e?.message || "Couldn't unblock. Try again.";
  } finally {
    unblockBusy.value = '';
  }
}

// ── Danger ──────────────────────────────────────────────────────────────
const disconnectAction = useAction(async () => {
  must(await disconnect(), "Couldn't disconnect. Check your connection and try again.");
  router.replace('/tabs/pager');
}, { weight: 'pop' });

async function askDisconnect() {
  const base = 'This deletes your pager number, your friendships and any undelivered pages from our server, and every page on this phone. Type DISCONNECT to confirm.';
  const a = await alertController.create({
    header: 'Disconnect this number?',
    message: base,
    inputs: [{ name: 'confirm', type: 'text', placeholder: 'DISCONNECT', attributes: { autocapitalize: 'characters', autocomplete: 'off', 'aria-label': 'Type DISCONNECT to confirm' } }],
    buttons: [
      { text: 'Cancel', role: 'cancel' },
      {
        text: 'Disconnect',
        role: 'destructive',
        handler: (values) => {
          if (String(values?.confirm || '').trim() !== 'DISCONNECT') {
            a.message = `${base} (Type DISCONNECT exactly, in capitals.)`;
            return false;
          }
          return true;
        },
      },
    ],
  });
  await a.present();
  const { role } = await a.onDidDismiss();
  if (role === 'destructive') disconnectAction.run();
}
</script>
